import { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import {
	LOAN_PURPOSE_MERCHANT_CASH_ADVANCE,
	LOAN_PURPOSE_BUSINESS_TERM_ADVANCE,
	LOAN_PURPOSE_UNSURE,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export const AdditionalInfo = () => {
	const { formValues, setFormValue, setFormError } = useFormDataStore();
	const { enabledFields, setFormErrors, setValidateFormDetails, monthlyTurnoverMin } =
		useFormStateStore();

	const { loanPurpose, abnAge, ownershipTime, businessName, monthlyTurnover } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		abnAge: false,
		ownershipTime: false,
		monthlyTurnover: false,
		businessName: false,
	});

	const handleAbnAgeChange = useCallback(
		(value: string) => {
			setFormValue("abnAge", value);
			setErrors((prev) => ({
				...prev,
				abnAge: !value,
			}));
		},
		[setFormValue],
	);

	const handleOwnershipTimeChange = useCallback(
		(value: string) => {
			setFormValue("ownershipTime", value);
			setErrors((prev) => ({
				...prev,
				ownershipTime: !value,
			}));
		},
		[setFormValue],
	);

	const handleMonthlyTurnoverChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("monthlyTurnover", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				monthlyTurnover: formattedValue < monthlyTurnoverMin,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handleAbnChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const businessName = e.currentTarget.value;

			setFormValue("businessName", businessName);

			setErrors((prev) => ({
				...prev,
				businessName: !businessName.trim(),
			}));
		},
		[setFormValue],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			abnAge: enabledFields.abnAge?.length === 0 ? false : !abnAge,
			ownershipTime: enabledFields.ownershipTime?.length === 0 ? false : !ownershipTime,
			monthlyTurnover: !loanPurpose
				? false
				: !monthlyTurnover ||
					parseFloat(monthlyTurnover.replace(/,/g, "")) < monthlyTurnoverMin,
			businessName: !businessName?.trim(),
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [abnAge, monthlyTurnover, businessName, ownershipTime]);

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		// clearFormError(field as string);
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	return (
		<div className="jF-space-y-4">
			{enabledFields.abnAge?.length != 0 &&
				[
					LOAN_PURPOSE_MERCHANT_CASH_ADVANCE,
					LOAN_PURPOSE_BUSINESS_TERM_ADVANCE,
					LOAN_PURPOSE_UNSURE,
				].includes(formValues.loanPurpose) && (
					<>
						<RadioGroupField
							id="radioAbnAge"
							label="How long has your ABN been running?"
							value={abnAge || ""}
							onChange={handleAbnAgeChange}
							required
							error={errors.abnAge ? "This field is required" : ""}
							gridCols={2}
							showIcons={false}
							className="jF-mt-4 jF-justify-center jF-w-full"
							options={enabledFields.abnAge
								.filter((s) => s.value)
								.map((s) => ({
									id: s.value,
									value: s.value,
									label: s.option,
								}))}
						/>

						{enabledFields.ownershipTime?.length != 0 && (
							<RadioGroupField
								id="radioOwnershipTime"
								label="Have you owned the company for more than 6 months?"
								value={ownershipTime || ""}
								onChange={handleOwnershipTimeChange}
								required
								showIcons={false}
								error={errors.ownershipTime ? "This field is required" : ""}
								gridCols={2}
								className="jF-mt-4 jF-justify-center jF-w-full"
								options={enabledFields.ownershipTime
									.filter((s) => s.value)
									.map((s) => ({
										id: s.value,
										value: s.value,
										label: s.option,
									}))}
							/>
						)}
					</>
				)}

			{enabledFields.abnAge?.length != 0 && (
				<div>
					<label
						htmlFor="businessName"
						className="jF-block jF-text-md jF-font-semibold jF-leading-6 jF-text-gray-900"
					>
						Enter your registered business trading name:
					</label>
					<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
						<input
							value={businessName}
							type="text"
							id="businessName"
							aria-describedby="businessName"
							aria-label="Business Name"
							placeholder="e.g. Acme Trading Pty Ltd"
							onChange={handleAbnChange}
							className={clsx(
								errors.businessName
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
							)}
						/>
					</div>
					{errors.businessName && (
						<span className="jF-text-xs jF-text-red-500">
							Business trading name is required
						</span>
					)}
				</div>
			)}

			{
				<div>
					<label
						htmlFor="grossIncome"
						className="jF-block jF-text-md jF-font-semibold jF-leading-6 jF-text-gray-900"
					>
						Average Monthly Turnover
					</label>
					<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<span className="jF-text-gray-500 sm:jF-text-md">$</span>
						</div>
						<input
							value={monthlyTurnover}
							type="text"
							id="grossIncome"
							aria-describedby="monthlyTurnover"
							inputMode="numeric"
							aria-label="Combined Income"
							maxLength={12}
							onChange={handleMonthlyTurnoverChange}
							onFocus={() => handleFocus("monthlyTurnover")}
							className={clsx(
								errors.monthlyTurnover
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
							)}
						/>
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
							<span className="jF-text-gray-500 sm:jF-text-sm">AUD</span>
						</div>
					</div>
					{errors.monthlyTurnover && (
						<span className="jF-text-xs jF-text-red-500">
							Minimum monthly turnover is ${monthlyTurnoverMin.toLocaleString()}
						</span>
					)}
				</div>
			}
		</div>
	);
};
